import { apiClient } from '@/api/apiClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Quarter } from '@/config/cbamReportingPeriods'

export type ImportedGood = {
  id: string
  customId?: string
  createdAt: string
  updatedAt?: string
  deletedAt?: string
  country: string
  cnCode: string
  productionMethodCode: string
  description?: string
  importDate?: string
  quarter?: Quarter | null
  netMassInTonnes: number
  isUsingDefaultEmissions: boolean
  defaultSpecificDirectEmbeddedEmissions?: number
  defaultSpecificIndirectEmbeddedEmissions?: number
  defaultElectricityConsumptionMWh?: number
  requestedCustomsProcedure?: string
  previousCustomsProcedure?: string
  inwardProcessingInformation?: {
    memberState: string
    inwardProcessingWaiver: boolean
    authorisation: string
    globalisationTimeStart: string
    globalisationTimeEnd: string
    billOfDischargeDeadline: string
  }[]
  inwardProcessedNetMassInTonnes?: number
  installationId?: string
  cbamReportId: string
  partnershipId?: string
  supplierEmissionsReportId?: string
  importerId?: string
  sellerName?: string
  goodsReceivedReportId?: string | null
}

type CreateImportedGoodPayload = {
  customId?: string
  cnCode: string
  country: string
  sellerName?: string
  description?: string
  importDate?: string
  netMassInTonnes: number
  isUsingDefaultEmissions: boolean
  defaultSpecificDirectEmbeddedEmissions?: number
  defaultSpecificIndirectEmbeddedEmissions?: number
  defaultElectricityConsumptionMWh?: number
  requestedCustomsProcedure?: string
  previousCustomsProcedure?: string
  inwardProcessingInformation?: {
    memberState: string
    inwardProcessingWaiver: boolean
    authorisation: string
    globalisationTimeStart: string
    globalisationTimeEnd: string
    billOfDischargeDeadline: string
  }[]
  inwardProcessedNetMassInTonnes?: number
  installationId?: string
  partnershipId?: string
  supplierEmissionsReportId?: string
  importerId?: string
}

type UpdateImportedGoodPayload = {
  customId?: string
  cnCode: string
  country: string
  sellerName?: string
  description?: string
  importDate?: string
  quarter?: Quarter | null
  netMassInTonnes: number
  isUsingDefaultEmissions: boolean
  defaultSpecificDirectEmbeddedEmissions?: number
  defaultSpecificIndirectEmbeddedEmissions?: number
  defaultElectricityConsumptionMWh?: number
  requestedCustomsProcedure?: string
  previousCustomsProcedure?: string
  inwardProcessingInformation?: {
    memberState: string
    inwardProcessingWaiver: boolean
    authorisation: string
    globalisationTimeStart: string
    globalisationTimeEnd: string
    billOfDischargeDeadline: string
  }[]
  inwardProcessedNetMassInTonnes?: number
  installationId?: string
  partnershipId?: string
  supplierEmissionsReportId?: string
  importerId?: string
}

export type InwardProcessingInfo = {
  memberState: string
  inwardProcessingWaiver: boolean
  authorisation: string
  globalisationTimeStart: string
  globalisationTimeEnd: string
  billOfDischargeDeadline: string
}

export type BulkUpdateImportedGoodsUpdates = {
  importerId?: string | null
  partnershipId?: string | null
  installationId?: string | null
  supplierEmissionsReportId?: string | null
  requestedCustomsProcedure?: string | null
  previousCustomsProcedure?: string | null
  inwardProcessedNetMassInTonnes?: number | null
  inwardProcessingInformation?: InwardProcessingInfo[] | null
}

type BulkUpdateImportedGoodsPayload = {
  ids: string[]
  updates: BulkUpdateImportedGoodsUpdates
}

type BulkDeleteImportedGoodsPayload = {
  ids: string[]
}

const queryKeyImportedGoods = 'importedGoods'
const queryKeyGoodsReceivedReports = 'goodsReceivedReports'

export const goodsReceivedReportsQueryKey = (organizationId: string, cbamReportId: string) =>
  [queryKeyGoodsReceivedReports, organizationId, cbamReportId] as const

const queryKeyGoodsReceivedReportDownloadUrl = 'goodsReceivedReportDownloadUrl'

export const goodsReceivedReportDownloadUrlQueryKey = (
  organizationId: string,
  cbamReportId: string,
  goodsReceivedReportId: string
) =>
  [
    queryKeyGoodsReceivedReportDownloadUrl,
    organizationId,
    cbamReportId,
    goodsReceivedReportId
  ] as const

export const fetchGoodsReceivedReportDownloadUrl = async (
  organizationId: string,
  cbamReportId: string,
  goodsReceivedReportId: string
) => {
  const response = await apiClient.get<{ url: string; fileName: string }>(
    `/v2/organizations/${organizationId}/cbam-reports/${cbamReportId}/goods-received-reports/${goodsReceivedReportId}/download-url`
  )
  return response.data
}

export const useGoodsReceivedReportDownloadUrl = (
  organizationId: string,
  cbamReportId: string,
  goodsReceivedReportId: string | null | undefined,
  enabled: boolean
) => {
  return useQuery({
    queryKey: goodsReceivedReportDownloadUrlQueryKey(
      organizationId,
      cbamReportId,
      goodsReceivedReportId ?? ''
    ),
    queryFn: () =>
      fetchGoodsReceivedReportDownloadUrl(
        organizationId,
        cbamReportId,
        goodsReceivedReportId!
      ),
    enabled: enabled && Boolean(goodsReceivedReportId),
    staleTime: 50 * 60 * 1000
  })
}

export const useGetAllImportedGoods = (organizationId: string) => {
  return useQuery({
    queryKey: [queryKeyImportedGoods, organizationId],
    queryFn: async () => {
      const response = await apiClient.get<ImportedGood[]>(
        `/v2/organizations/${organizationId}/imported-goods`
      )
      return response.data
    }
  })
}

export type GoodsReceivedReport = {
  id: string
  uploadedAt: string
  fileName: string
  fileFormat: string
  status: 'UPLOADED'
  uploadedByUserId: string
  cbamReportId: string
}

export const useGetGoodsReceivedReports = (organizationId: string, cbamReportId: string) => {
  return useQuery({
    queryKey: goodsReceivedReportsQueryKey(organizationId, cbamReportId),
    queryFn: async () => {
      const response = await apiClient.get<GoodsReceivedReport[]>(
        `/v2/organizations/${organizationId}/cbam-reports/${cbamReportId}/goods-received-reports`
      )
      return response.data
    },
    enabled: Boolean(organizationId && cbamReportId)
  })
}

export const useCreateImportedGood = (organizationId: string, cbamReportId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [queryKeyImportedGoods, organizationId, cbamReportId],
    mutationFn: async (payload: CreateImportedGoodPayload) => {
      const response = await apiClient.post<ImportedGood>(
        `/v2/organizations/${organizationId}/cbam-reports/${cbamReportId}/imported-goods`,
        payload
      )

      queryClient.setQueryData<ImportedGood[]>([queryKeyImportedGoods, organizationId], oldData => [
        ...(oldData || []),
        response.data
      ])
      return response.data
    }
  })
}

export const useUpdateImportedGood = (organizationId: string, importedGoodId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [queryKeyImportedGoods, organizationId, importedGoodId],
    mutationFn: async (payload: UpdateImportedGoodPayload) => {
      const response = await apiClient.put<ImportedGood>(
        `/v2/organizations/${organizationId}/imported-goods/${importedGoodId}`,
        payload
      )

      queryClient.setQueryData<ImportedGood[]>(
        [queryKeyImportedGoods, organizationId],
        oldData =>
          oldData?.map(importedGood =>
            importedGood.id === importedGoodId ? response.data : importedGood
          ) || []
      )
      return response.data
    }
  })
}

export const useDeleteImportedGood = (organizationId: string, importedGoodId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [queryKeyImportedGoods, organizationId, importedGoodId],
    mutationFn: async () => {
      await apiClient.delete(`/v2/organizations/${organizationId}/imported-goods/${importedGoodId}`)

      queryClient.setQueryData<ImportedGood[]>(
        [queryKeyImportedGoods, organizationId],
        oldData => oldData?.filter(importedGood => importedGood.id !== importedGoodId) || []
      )
    }
  })
}

export const useBulkUpdateImportedGoods = (organizationId: string, cbamReportId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [queryKeyImportedGoods, organizationId, cbamReportId, 'bulk'],
    mutationFn: async (payload: BulkUpdateImportedGoodsPayload) => {
      const response = await apiClient.patch<ImportedGood[]>(
        `/v2/organizations/${organizationId}/cbam-reports/${cbamReportId}/imported-goods/bulk`,
        payload,
        { timeout: 300000 } // 5 minutes — bulk operations can exceed default client timeout
      )

      // Update the cache with the returned updated goods
      queryClient.setQueryData<ImportedGood[]>([queryKeyImportedGoods, organizationId], oldData => {
        if (!oldData) {
          return response.data
        }
        const updatedMap = new Map(response.data.map(good => [good.id, good]))
        return oldData.map(good => updatedMap.get(good.id) || good)
      })

      return response.data
    }
  })
}

export const useBulkDeleteImportedGoods = (organizationId: string, cbamReportId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [queryKeyImportedGoods, organizationId, cbamReportId, 'bulk-delete'],
    mutationFn: async (payload: BulkDeleteImportedGoodsPayload) => {
      await apiClient.delete(
        `/v2/organizations/${organizationId}/cbam-reports/${cbamReportId}/imported-goods/bulk`,
        {
          data: payload,
          timeout: 300000 // 5 minutes — bulk operations can exceed default client timeout
        }
      )

      // Remove deleted goods from the cache
      queryClient.setQueryData<ImportedGood[]>([queryKeyImportedGoods, organizationId], oldData => {
        if (!oldData) {
          return []
        }
        const deletedIds = new Set(payload.ids)
        return oldData.filter(good => !deletedIds.has(good.id))
      })
    }
  })
}

type BulkCreateImportedGoodsPayload = {
  items: CreateImportedGoodPayload[]
}

type BulkCreateImportedGoodsResult = {
  created: ImportedGood[]
  errors: { index: number; message: string }[]
}

export const useBulkCreateImportedGoods = (organizationId: string, cbamReportId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [queryKeyImportedGoods, organizationId, cbamReportId, 'bulk-create'],
    mutationFn: async (payload: BulkCreateImportedGoodsPayload) => {
      const response = await apiClient.post<BulkCreateImportedGoodsResult>(
        `/v2/organizations/${organizationId}/cbam-reports/${cbamReportId}/imported-goods/bulk`,
        payload,
        { timeout: 300000 } // 5 minutes — bulk operations can exceed default client timeout
      )

      // Only add successfully created goods to cache
      queryClient.setQueryData<ImportedGood[]>([queryKeyImportedGoods, organizationId], oldData => [
        ...(oldData || []),
        ...response.data.created
      ])

      return response.data
    }
  })
}

type FinalizeGrrImportedGoodsResult = BulkCreateImportedGoodsResult

export const useFinalizeGrrImportedGoods = (organizationId: string, cbamReportId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [queryKeyImportedGoods, organizationId, cbamReportId, 'finalize-grr'],
    mutationFn: async (goodsReceivedReportId: string) => {
      const response = await apiClient.post<FinalizeGrrImportedGoodsResult>(
        `/v2/organizations/${organizationId}/cbam-reports/${cbamReportId}/goods-received-reports/${goodsReceivedReportId}/finalize-imported-goods`,
        {},
        { timeout: 300000 }
      )
      return response.data
    },
    onSuccess: data => {
      queryClient.setQueryData<ImportedGood[]>([queryKeyImportedGoods, organizationId], oldData => [
        ...(oldData || []),
        ...data.created
      ])
    }
  })
}
